import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import axios from 'axios';
import { useSearch } from '../components/Layout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideRight = keyframes`
  from { width: 0; }
  to   { width: var(--bar-width); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.94) translateY(28px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const popIn = keyframes`
  0%   { transform: scale(0);    opacity: 0; }
  70%  { transform: scale(1.18); opacity: 1; }
  100% { transform: scale(1); }
`;

const trendBounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-3px); }
`;

// ═══════════════════════════════════════════════════════════════
// BREAKPOINTS
// ═══════════════════════════════════════════════════════════════

const bp = {
  xs: '360px',
  sm: '480px',
  md: '640px',
  lg: '768px',
  xl: '1024px',
};

// ═══════════════════════════════════════════════════════════════
// LAYOUT
// ═══════════════════════════════════════════════════════════════

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f0f6ff;
  padding: 20px 16px 100px;

  @media (min-width: ${bp.lg}) {
    padding: 36px 24px 80px;
  }
`;

const Container = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  animation: ${fadeUp} 0.5s ease-out both;
`;

// ═══════════════════════════════════════════════════════════════
// HEADER
// ═══════════════════════════════════════════════════════════════

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 12px;
  flex-wrap: wrap;

  @media (min-width: ${bp.lg}) {
    margin-bottom: 36px;
    align-items: center;
    flex-wrap: nowrap;
  }
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;

  h1 {
    font-size: 1.5rem;
    font-weight: 800;
    color: #0c2d48;
    margin: 0 0 4px;
    letter-spacing: -0.3px;
    display: flex;
    align-items: center;
    gap: 10px;

    @media (min-width: ${bp.lg}) {
      font-size: 2rem;
      margin-bottom: 6px;
    }
  }

  p {
    font-size: 0.85rem;
    color: #64748b;
    margin: 0;

    @media (min-width: ${bp.lg}) {
      font-size: 0.95rem;
    }
  }
`;

const HeaderIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: linear-gradient(135deg, #2563eb, #0ea5e9);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    fill: none;
    stroke: white;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  @media (min-width: ${bp.lg}) {
    width: 44px;
    height: 44px;
    border-radius: 12px;

    svg { width: 24px; height: 24px; }
  }
`;

// ═══════════════════════════════════════════════════════════════
// TOOLBAR
// ═══════════════════════════════════════════════════════════════

const ToolBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (min-width: ${bp.lg}) {
    gap: 12px;
    margin-bottom: 28px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 10px 14px 10px 38px;
  border: 1.5px solid #dbeafe;
  border-radius: 10px;
  font-size: 0.88rem;
  background: white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='15' height='15' fill='none' stroke='%2394a3b8' stroke-width='2' viewBox='0 0 24 24'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'/%3E%3C/svg%3E") 12px center no-repeat;
  color: #1e3a5f;
  -webkit-appearance: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
  }

  &::placeholder { color: #94a3b8; }

  @media (min-width: ${bp.lg}) {
    font-size: 0.92rem;
    padding: 11px 16px 11px 42px;
    border-radius: 12px;
  }
`;

const FilterSelect = styled.select`
  padding: 10px 12px;
  border: 1.5px solid #dbeafe;
  border-radius: 10px;
  font-size: 0.88rem;
  background: white;
  color: #1e3a5f;
  cursor: pointer;
  min-width: 130px;
  -webkit-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%2394a3b8' stroke-width='2' viewBox='0 0 24 24'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 28px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #2563eb;
  }

  @media (min-width: ${bp.lg}) {
    font-size: 0.92rem;
    padding: 11px 32px 11px 16px;
    border-radius: 12px;
    min-width: 160px;
  }
`;

// ═══════════════════════════════════════════════════════════════
// CLIENT CARDS
// ═══════════════════════════════════════════════════════════════

const ClientsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 32px;

  @media (min-width: ${bp.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${bp.xl}) {
    grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
    gap: 18px;
  }
`;

const ClientCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
  border: 1.5px solid #e8f0fe;
  cursor: pointer;
  transition: transform 0.22s cubic-bezier(0.22,1,0.36,1), box-shadow 0.22s, border-color 0.2s;
  animation: ${fadeUp} 0.4s ease-out both;
  animation-delay: ${({ index }) => index * 0.04}s;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 14px;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #2563eb, #0ea5e9);
    opacity: 0;
    transition: opacity 0.22s;
  }

  &:active {
    transform: scale(0.98);
    box-shadow: 0 4px 14px rgba(37,99,235,0.1);
  }

  @media (hover: hover) {
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 10px 28px rgba(37,99,235,0.12);
      border-color: #bfdbfe;

      &::before { opacity: 1; }
    }
  }

  @media (min-width: ${bp.lg}) {
    flex-direction: column;
    align-items: flex-start;
    padding: 22px;
    border-radius: 18px;
  }
`;

const CardAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #2563eb, #38bdf8);
  color: white;
  font-size: 1rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: -0.5px;

  @media (min-width: ${bp.lg}) {
    width: 48px;
    height: 48px;
    font-size: 1.15rem;
    margin-bottom: 12px;
  }
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;

  @media (min-width: ${bp.lg}) {
    width: 100%;
  }
`;

const ClientName = styled.h3`
  font-size: 0.95rem;
  font-weight: 700;
  color: #0c2d48;
  margin: 0 0 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: ${bp.lg}) {
    font-size: 1.05rem;
    margin-bottom: 10px;
  }
`;

const ClientMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;

  @media (min-width: ${bp.lg}) {
    gap: 5px;
    margin-bottom: 14px;
  }
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.78rem;
  color: #64748b;
  gap: 8px;

  .key { font-weight: 500; white-space: nowrap; }
  .val {
    font-weight: 600;
    color: #1e3a5f;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: right;
    max-width: 55%;
  }

  @media (min-width: ${bp.lg}) {
    font-size: 0.82rem;
  }
`;

const TestsBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(135deg, #2563eb, #0ea5e9);
  color: white;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;

  @media (min-width: ${bp.lg}) {
    font-size: 0.78rem;
    padding: 4px 10px;
  }
`;

const AutoMergeNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 18px;
  padding: 12px 14px;
  border: 1px solid #bbf7d0;
  border-radius: 14px;
  background: linear-gradient(135deg, #f0fdf4, #ecfeff);
  color: #166534;
  font-size: 0.84rem;
  font-weight: 700;
  box-shadow: 0 8px 24px rgba(22, 101, 52, 0.08);
  animation: ${fadeIn} 0.35s ease both;

  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 999px;
    background: #22c55e;
    color: white;
    flex-shrink: 0;
    font-size: 0.78rem;
  }

  .sub {
    display: block;
    margin-top: 2px;
    color: #15803d;
    font-size: 0.76rem;
    font-weight: 600;
  }
`;

// ═══════════════════════════════════════════════════════════════
// EMPTY / LOADING
// ═══════════════════════════════════════════════════════════════

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 20px;
  animation: ${fadeIn} 0.4s ease;

  h3 {
    font-size: 1.1rem;
    color: #0c2d48;
    margin: 14px 0 6px;
    font-weight: 700;
  }

  p { color: #64748b; margin: 0; font-size: 0.88rem; }
`;

const SpinnerRing = styled.div`
  width: 44px;
  height: 44px;
  border: 4px solid #dbeafe;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: ${spin} 0.75s linear infinite;
  margin: 0 auto 14px;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;

  svg {
    width: 32px;
    height: 32px;
    stroke: #93c5fd;
    fill: none;
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

// ═══════════════════════════════════════════════════════════════
// MERGE BUTTON
// ═══════════════════════════════════════════════════════════════

const MergeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  padding: 9px 14px;
  background: linear-gradient(135deg, #1d4ed8, #0ea5e9);
  color: #ffffff;
  border: 1.5px solid transparent;
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 6px 18px rgba(37,99,235,0.24);
  transition: all 0.22s;
  touch-action: manipulation;

  svg {
    width: 15px;
    height: 15px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:active { transform: scale(0.96); }

  @media (hover: hover) {
    &:hover:not(:disabled) {
      box-shadow: 0 10px 24px rgba(37,99,235,0.34);
      transform: translateY(-1px);
      filter: saturate(1.08);
    }
  }

  &:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }

  @media (min-width: ${bp.lg}) {
    margin-left: auto;
    min-width: 148px;
    font-size: 0.94rem;
    padding: 12px 22px;
    border-radius: 13px;

    svg { width: 17px; height: 17px; }
  }
`;

// ═══════════════════════════════════════════════════════════════
// MODAL SHELL — centred card on desktop and mobile
// ═══════════════════════════════════════════════════════════════

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10, 28, 55, 0.58);
  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease;

  @media (min-width: ${bp.lg}) {
    padding: 24px;
  }
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 16px;
  width: min(100%, ${({ wide }) => {
    if (wide === 'graph') return '430px';
    if (wide) return '430px';
    return '360px';
  }});
  max-height: calc(100dvh - 20px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 70px rgba(10,28,55,0.24);
  animation: ${scaleIn} 0.32s cubic-bezier(0.22,1,0.36,1) both;
  overflow: hidden;

  @media (min-width: ${bp.lg}) {
    border-radius: 24px;
    width: 100%;
    max-width: ${({ wide }) => {
      if (wide === 'graph') return '760px';
      if (wide) return '1040px';
      return '560px';
    }};
    max-height: 88vh;
    box-shadow: 0 32px 80px rgba(10,28,55,0.26);
  }
`;

const DragHandle = styled.div`
  display: none;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px 12px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
  gap: 10px;

  @media (max-width: ${bp.sm}) {
    padding: 10px 12px 9px;
  }

  @media (min-width: ${bp.lg}) {
    padding: 22px 28px 18px;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.05rem;
  font-weight: 800;
  color: #0c2d48;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;

  > svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    fill: none;
    flex-shrink: 0;
  }

  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span.sub {
    font-size: 0.72rem;
    font-weight: 500;
    color: #94a3b8;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    padding: 2px 8px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  @media (max-width: ${bp.sm}) {
    font-size: 0.86rem;
    gap: 6px;
    span.sub { font-size: 0.64rem; padding: 1px 6px; }
  }

  @media (min-width: ${bp.lg}) {
    font-size: 1.25rem;
    gap: 10px;

    > svg { width: 20px; height: 20px; }
    span.sub { font-size: 0.82rem; padding: 2px 10px; }
  }
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  flex-shrink: 0;
  transition: all 0.18s;
  touch-action: manipulation;

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2.5;
    stroke-linecap: round;
  }

  &:active {
    background: #eff6ff;
    border-color: #2563eb;
    color: #2563eb;
  }

  @media (hover: hover) {
    &:hover {
      border-color: #2563eb;
      color: #2563eb;
      background: #eff6ff;
    }
  }

  @media (min-width: ${bp.lg}) {
    width: 34px;
    height: 34px;
    border-radius: 9px;

    svg { width: 16px; height: 16px; }
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px 16px 24px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #dbeafe; border-radius: 2px; }

  @media (max-width: ${bp.sm}) {
    padding: 12px 12px 18px;
  }

  @media (min-width: ${bp.lg}) {
    padding: 24px 28px 32px;

    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-thumb { border-radius: 3px; }
  }
`;

// ═══════════════════════════════════════════════════════════════
// MERGE MODAL — dedicated scrollable list box for desktop
// ═══════════════════════════════════════════════════════════════

const MergeListBox = styled.div`
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  max-height: 260px;
  padding: 8px;
  background: #f8fafc;

  @media (min-width: ${bp.lg}) {
    max-height: 340px;
  }
`;

// ═══════════════════════════════════════════════════════════════
// DEMOGRAPHICS
// ═══════════════════════════════════════════════════════════════

const DemoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  background: linear-gradient(135deg, #f0f9ff 0%, #eff6ff 100%);
  padding: 12px;
  border-radius: 12px;
  border: 1px solid #dbeafe;
  margin-bottom: 20px;

  @media (max-width: ${bp.sm}) {
    gap: 6px;
    padding: 8px;
    margin-bottom: 14px;
  }

  @media (min-width: ${bp.md}) {
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    padding: 14px;
  }

  @media (min-width: ${bp.lg}) {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px;
    padding: 18px;
    margin-bottom: 28px;
  }
`;

const DemoCard = styled.div`
  background: white;
  border-radius: 9px;
  padding: 10px 12px;
  border: 1px solid #e8f0fe;

  .lbl {
    font-size: 0.62rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }

  .val {
    font-size: 0.85rem;
    font-weight: 700;
    color: #0c2d48;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: ${bp.sm}) {
    border-radius: 8px;
    padding: 7px 8px;
    .lbl { font-size: 0.54rem; margin-bottom: 3px; }
    .val { font-size: 0.7rem; }
  }

  @media (min-width: ${bp.lg}) {
    border-radius: 10px;
    padding: 12px 14px;

    .lbl { font-size: 0.68rem; }
    .val { font-size: 0.92rem; }
  }
`;

// ═══════════════════════════════════════════════════════════════
// SECTION HEADING
// ═══════════════════════════════════════════════════════════════

const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;

  .icon-wrap {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: ${({ color }) => color || '#eff6ff'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 15px;
      height: 15px;
      stroke: ${({ iconColor }) => iconColor || '#2563eb'};
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  h3 {
    font-size: 0.92rem;
    font-weight: 750;
    color: #0c2d48;
    margin: 0;
  }

  .count {
    font-size: 0.72rem;
    background: #f1f5f9;
    color: #64748b;
    border-radius: 20px;
    padding: 2px 8px;
    font-weight: 600;
    margin-left: auto;
  }

  @media (min-width: ${bp.lg}) {
    gap: 10px;
    margin-bottom: 18px;

    .icon-wrap {
      width: 32px;
      height: 32px;
      border-radius: 9px;

      svg { width: 17px; height: 17px; }
    }

    h3 { font-size: 1rem; }
    .count { font-size: 0.78rem; padding: 2px 9px; }
  }
`;

// ═══════════════════════════════════════════════════════════════
// TEST BLOCK
// ═══════════════════════════════════════════════════════════════

const TestBlock = styled.div`
  margin-bottom: 28px;
  animation: ${fadeUp} 0.4s ease-out both;
  animation-delay: ${({ delay }) => delay || 0}s;

  @media (min-width: ${bp.lg}) {
    margin-bottom: 36px;
  }
`;

const TestBlockHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 8px;
  flex-wrap: wrap;

  @media (min-width: ${bp.lg}) {
    margin-bottom: 14px;
  }
`;

const TestBlockTitle = styled.div`
  font-size: 0.88rem;
  font-weight: 800;
  color: #0c2d48;
  display: flex;
  align-items: center;
  gap: 7px;

  .dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2563eb, #0ea5e9);
    animation: ${popIn} 0.4s ease both;
    flex-shrink: 0;
  }

  @media (min-width: ${bp.lg}) {
    font-size: 0.98rem;
  }
`;

const AssessmentCount = styled.span`
  font-size: 0.72rem;
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
  border-radius: 20px;
  padding: 2px 9px;
  font-weight: 700;
`;

// ═══════════════════════════════════════════════════════════════
// COMPARISON TABLE
// ═══════════════════════════════════════════════════════════════

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);

  @media (min-width: ${bp.lg}) {
    border-radius: 14px;
  }
`;

const ScrollHint = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.7rem;
  color: #94a3b8;
  margin-bottom: 6px;

  svg {
    width: 13px;
    height: 13px;
    stroke: #94a3b8;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  @media (min-width: ${bp.xl}) {
    display: none;
  }
`;

const CompareTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 420px;
  background: white;
  font-size: 0.8rem;

  @media (max-width: ${bp.sm}) {
    min-width: 360px;
    font-size: 0.68rem;
  }

  @media (min-width: ${bp.lg}) {
    font-size: 0.86rem;
  }
`;

const THead = styled.thead`
  background: linear-gradient(90deg, #1e40af, #0369a1);
`;

const TH = styled.th`
  padding: 10px 12px;
  text-align: left;
  font-size: 0.68rem;
  font-weight: 700;
  color: rgba(255,255,255,0.92);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;

  &:first-of-type { min-width: 110px; }

  @media (max-width: ${bp.sm}) {
    padding: 7px 8px;
    font-size: 0.56rem;
    &:first-of-type { min-width: 82px; }
  }

  @media (min-width: ${bp.lg}) {
    padding: 12px 16px;
    font-size: 0.75rem;

    &:first-of-type { min-width: 130px; }
  }
`;

const TR = styled.tr`
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;
  animation: ${fadeUp} 0.35s ease both;
  animation-delay: ${({ delay }) => delay || 0}s;

  &:last-of-type { border-bottom: none; }

  @media (hover: hover) {
    &:hover { background: #f8fbff; }
  }
`;

const TD = styled.td`
  padding: 10px 12px;
  vertical-align: middle;
  color: #334155;

  @media (max-width: ${bp.sm}) {
    padding: 7px 8px;
  }

  @media (min-width: ${bp.lg}) {
    padding: 13px 16px;
  }
`;

const DateCell = styled.div`
  .main { font-weight: 700; color: #0c2d48; font-size: 0.82rem; }
  .sub  { font-size: 0.68rem; color: #94a3b8; margin-top: 1px; }

  @media (min-width: ${bp.lg}) {
    .main { font-size: 0.88rem; }
    .sub  { font-size: 0.75rem; }
  }
`;

const TotalScore = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  background: ${({ color }) => color}18;
  border: 1.5px solid ${({ color }) => color}44;
  border-radius: 9px;
  padding: 5px 10px;
  min-width: 48px;
  text-align: center;

  .num {
    font-size: 1.1rem;
    font-weight: 900;
    color: ${({ color }) => color};
    line-height: 1;
  }

  .lbl {
    font-size: 0.58rem;
    font-weight: 700;
    color: ${({ color }) => color};
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-top: 2px;
    opacity: 0.75;
  }

  @media (min-width: ${bp.lg}) {
    border-radius: 10px;
    padding: 6px 12px;
    min-width: 56px;

    .num { font-size: 1.25rem; }
    .lbl { font-size: 0.63rem; }
  }
`;

const SubCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 90px;

  @media (min-width: ${bp.lg}) {
    min-width: 110px;
  }
`;

const SubTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;

  .score {
    font-weight: 700;
    font-size: 0.8rem;
    color: #0c2d48;
  }

  .sev {
    font-size: 0.62rem;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 20px;
    background: ${({ color }) => color}18;
    color: ${({ color }) => color};
    border: 1px solid ${({ color }) => color}40;
    white-space: nowrap;
    text-transform: capitalize;
  }

  @media (min-width: ${bp.lg}) {
    gap: 6px;

    .score { font-size: 0.85rem; }
    .sev   { font-size: 0.68rem; padding: 2px 7px; }
  }
`;

const MiniBar = styled.div`
  height: 4px;
  background: #f1f5f9;
  border-radius: 2px;
  overflow: hidden;

  .fill {
    height: 100%;
    width: var(--pct);
    background: ${({ color }) => color};
    border-radius: 2px;
    animation: ${slideRight} 0.6s cubic-bezier(0.22,1,0.36,1) both;
    animation-delay: ${({ delay }) => delay || 0}s;
  }

  @media (min-width: ${bp.lg}) {
    height: 5px;
    border-radius: 3px;
  }
`;

const TrendPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
  animation: ${({ trend }) => trend !== 'stable' ? trendBounce : 'none'} 1.4s ease infinite;

  background: ${({ trend }) =>
    trend === 'improvement' ? '#f0fdf4' :
    trend === 'decline'     ? '#fff1f2' : '#f8fafc'};

  color: ${({ trend }) =>
    trend === 'improvement' ? '#16a34a' :
    trend === 'decline'     ? '#dc2626' : '#64748b'};

  border: 1px solid ${({ trend }) =>
    trend === 'improvement' ? '#bbf7d0' :
    trend === 'decline'     ? '#fecaca' : '#e2e8f0'};

  svg {
    width: 11px;
    height: 11px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  @media (min-width: ${bp.lg}) {
    font-size: 0.76rem;
    padding: 4px 10px;

    svg { width: 13px; height: 13px; }
  }
`;

// ─── Graph toggle button (inside assessment table header) ────────────────────
const GraphToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid #bfdbfe;
  background: linear-gradient(135deg, #eff6ff, #dbeafe);
  color: #1d4ed8;
  border-radius: 999px;
  padding: 6px 13px;
  font-size: 0.74rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.18s;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(37,99,235,0.10);

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:active {
    transform: scale(0.96);
  }

  @media (hover: hover) {
    &:hover {
      background: linear-gradient(135deg, #dbeafe, #bfdbfe);
      border-color: #93c5fd;
      box-shadow: 0 3px 10px rgba(37,99,235,0.18);
      transform: translateY(-1px);
    }
  }

  @media (min-width: ${bp.lg}) {
    font-size: 0.80rem;
    padding: 7px 15px;

    svg { width: 15px; height: 15px; }
  }
`;

// ─── Graph modal content ─────────────────────────────────────────────────────
const GraphInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;

  .title {
    font-size: 0.82rem;
    font-weight: 800;
    color: #1e3a8a;
  }

  .meta {
    font-size: 0.72rem;
    color: #64748b;
    white-space: nowrap;
    background: #f1f5f9;
    border-radius: 20px;
    padding: 2px 9px;
    font-weight: 600;
  }
`;

const GraphSvgWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fbff;
  padding: 8px 4px 4px;

  svg {
    min-width: 100%;
    height: auto;
  }
`;

const GraphLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  color: #1d4ed8;
  font-size: 0.72rem;
  font-weight: 700;

  .dot {
    width: 9px;
    height: 9px;
    border-radius: 999px;
    background: #2563eb;
  }
`;

// ═══════════════════════════════════════════════════════════════
// NO DATA
// ═══════════════════════════════════════════════════════════════

const NoData = styled.div`
  text-align: center;
  padding: 36px 20px;
  animation: ${fadeIn} 0.4s ease;
  color: #94a3b8;

  svg {
    width: 44px;
    height: 44px;
    stroke: #bfdbfe;
    fill: none;
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    margin-bottom: 12px;
  }

  p { margin: 0; font-size: 0.88rem; font-weight: 500; }
`;

// ═══════════════════════════════════════════════════════════════
// MERGE MODAL ITEMS
// ═══════════════════════════════════════════════════════════════

const MergeClientRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 11px;
  border-radius: 10px;
  border: 1.5px solid ${({ checked }) => checked ? '#bfdbfe' : 'transparent'};
  background: ${({ checked }) => checked ? '#eff6ff' : 'white'};
  margin-bottom: 6px;
  cursor: pointer;
  transition: border-color 0.18s, background 0.18s;
  touch-action: manipulation;

  &:last-child { margin-bottom: 0; }
  &:active { opacity: 0.85; }

  @media (hover: hover) {
    &:hover { border-color: #93c5fd; background: #f0f9ff; }
  }
`;

const MergeCheckbox = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 2px solid ${({ checked }) => checked ? '#2563eb' : '#cbd5e1'};
  background: ${({ checked }) => checked ? '#2563eb' : 'white'};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  svg {
    width: 12px;
    height: 12px;
    stroke: white;
    fill: none;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    opacity: ${({ checked }) => checked ? 1 : 0};
    transition: opacity 0.15s;
  }
`;

const MergeClientInfo = styled.div`
  flex: 1;
  min-width: 0;

  .name {
    font-weight: 700;
    color: #0c2d48;
    font-size: 0.88rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .detail { font-size: 0.74rem; color: #94a3b8; margin-top: 2px; }
`;

const TargetSelect = styled.select`
  width: 100%;
  padding: 10px 32px 10px 12px;
  border: 1.5px solid #dbeafe;
  border-radius: 10px;
  font-size: 0.88rem;
  color: #0c2d48;
  background: white;
  -webkit-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%2394a3b8' stroke-width='2' viewBox='0 0 24 24'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  margin-top: 6px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
  }
`;

const MergeActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;

  @media (max-width: ${bp.sm}) {
    flex-direction: column-reverse;

    button { width: 100%; justify-content: center; }
  }
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 11px 20px;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  touch-action: manipulation;

  &.ghost {
    background: #f1f5f9;
    color: #475569;

    &:active { background: #e2e8f0; }

    @media (hover: hover) { &:hover { background: #e2e8f0; } }
  }

  &.primary {
    background: linear-gradient(135deg, #2563eb, #0ea5e9);
    color: white;
    box-shadow: 0 2px 10px rgba(37,99,235,0.28);

    &:active { transform: scale(0.97); }

    @media (hover: hover) {
      &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 16px rgba(37,99,235,0.35);
      }
    }

    &:disabled { opacity: 0.45; cursor: not-allowed; }
  }
`;

// ═══════════════════════════════════════════════════════════════
// SVG ICONS
// ═══════════════════════════════════════════════════════════════

const IconUsers = () => (
  <svg viewBox="0 0 24 24">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const IconMerge = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="18" r="3"/>
    <circle cx="6"  cy="6"  r="3"/>
    <circle cx="6"  cy="18" r="3"/>
    <path d="M6 9v6M15.5 6.5 9 12l6.5 5.5"/>
  </svg>
);

const IconChart = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6"  y1="20" x2="6"  y2="14"/>
    <line x1="2"  y1="20" x2="22" y2="20"/>
  </svg>
);

const IconX = () => (
  <svg viewBox="0 0 24 24">
    <line x1="18" y1="6"  x2="6"  y2="18"/>
    <line x1="6"  y1="6"  x2="18" y2="18"/>
  </svg>
);

const IconCheck = () => (
  <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
);

const IconTrendUp = () => (
  <svg viewBox="0 0 24 24">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
    <polyline points="17 6 23 6 23 12"/>
  </svg>
);

const IconTrendDown = () => (
  <svg viewBox="0 0 24 24">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
    <polyline points="17 18 23 18 23 12"/>
  </svg>
);

const IconMinus = () => (
  <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/></svg>
);

const IconClipboard = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
  </svg>
);

const IconInbox = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
  </svg>
);

const IconSwipe = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <polyline points="5 12 19 12"/>
    <polyline points="13 6 19 12 13 18"/>
  </svg>
);

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

const Clients = () => {
  const { searchQuery } = useSearch();
  const [clients, setClients]                     = useState([]);
  const [filteredClients, setFilteredClients]     = useState([]);
  const [loading, setLoading]                     = useState(true);
  const [selectedClient, setSelectedClient]       = useState(null);
  const [clientTestHistory, setClientTestHistory] = useState([]);
  const [testFilter, setTestFilter]               = useState('');
  const [tests, setTests]                         = useState([]);
  const [showMergeModal, setShowMergeModal]       = useState(false);
  const [selectedForMerge, setSelectedForMerge]   = useState([]);
  const [mergeTarget, setMergeTarget]             = useState('');
  const [autoMergeSummary, setAutoMergeSummary]   = useState(null);
  // graphModal: null | { testName: string, results: array }
  const [graphModal, setGraphModal]               = useState(null);

  // ── helpers ──────────────────────────────────────────────────
  const getClientGenderDisplay = (client) => {
    const gender       = String(client?.gender       || '').trim();
    const genderCustom = String(client?.genderCustom || '').trim();
    if (gender.toLowerCase() === 'other' && genderCustom) return genderCustom;
    return genderCustom || gender || 'Not specified';
  };

  const initials = (c) =>
    `${c.firstName?.[0] || ''}${c.lastName?.[0] || ''}`.toUpperCase();

  // ── data loading ─────────────────────────────────────────────
  useEffect(() => { loadClients(); loadTests(); }, []);
  useEffect(() => { filterClients(); }, [searchQuery, clients, testFilter]); // eslint-disable-line

  const loadClients = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/practitioner/clients`);
      setClients(res.data.clients || []);
      setAutoMergeSummary(res.data.autoMerge || null);
    } catch (e) {
      console.error('Failed to load clients:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadTests = async () => {
    try {
      const res = await axios.get(`${API_URL}/practitioner/tests`);
      setTests(res.data || []);
    } catch (e) {
      console.error('Failed to load tests:', e);
    }
  };

  const filterClients = () => {
    let filtered = clients;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q)
      );
    }
    if (testFilter) {
      filtered = filtered.filter(c =>
        c.testIds?.includes(testFilter) ||
        c.recentTests?.some(t => t.testId === testFilter)
      );
    }
    setFilteredClients(filtered);
  };

  // ── merge ────────────────────────────────────────────────────
  const toggleSelectForMerge = (client) => {
    setSelectedForMerge(prev =>
      prev.find(c => c.id === client.id)
        ? prev.filter(c => c.id !== client.id)
        : [...prev, client]
    );
  };

  const mergeClients = async () => {
    if (!mergeTarget || selectedForMerge.length < 2) {
      alert('Please select a target client and at least one client to merge');
      return;
    }
    try {
      const sourceIds = selectedForMerge
        .filter(c => c.id !== mergeTarget)
        .map(c => c.id);
      await axios.post(`${API_URL}/practitioner/clients/merge`, {
        targetClientId: mergeTarget,
        sourceClientIds: sourceIds,
      });
      alert('Clients merged successfully!');
      setShowMergeModal(false);
      setSelectedForMerge([]);
      setMergeTarget('');
      loadClients();
    } catch (e) {
      alert('Failed to merge clients: ' + (e.response?.data?.error || e.message));
    }
  };

  // ── client detail ────────────────────────────────────────────
  const viewClientDetails = async (client) => {
    setSelectedClient(client);
    setClientTestHistory([]);
    try {
      const res = await axios.get(`${API_URL}/practitioner/clients/${client.id}/history`);
      setClientTestHistory(res.data.history || []);
    } catch (e) {
      console.error('Failed to load client history:', e);
    }
  };

  const groupTestsByType = () => {
    const grouped = {};
    clientTestHistory.forEach(result => {
      const name = result.TestCatalog?.name || 'Unknown Test';
      if (!grouped[name]) grouped[name] = [];
      grouped[name].push(result);
    });
    Object.keys(grouped).forEach(k => {
      grouped[k].sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
    });
    return grouped;
  };

  // ── scoring helpers ───────────────────────────────────────────

  const getResponseValueByItemId = (responses, itemId) => {
    if (!responses) return null;
    const keysToTry = [itemId, String(itemId)];

    for (const key of keysToTry) {
      const value = responses[key];
      if (value !== undefined && value !== null && value !== '') {
        const num = Number(value);
        if (!Number.isNaN(num)) return num;
      }
    }

    if (Array.isArray(responses)) {
      const match = responses.find(entry =>
        entry && (entry.itemId === itemId || entry.questionId === itemId || entry.id === itemId)
      );
      if (match) {
        const v = match.value ?? match.score ?? match.response;
        const num = Number(v);
        if (!Number.isNaN(num)) return num;
      }
    }

    return null;
  };

  const getNumericScoreValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    if (typeof value === 'object') {
      const candidates = [value.final, value.score, value.raw, value.value];
      for (const candidate of candidates) {
        const parsed = getNumericScoreValue(candidate);
        if (parsed !== null) return parsed;
      }
    }
    return null;
  };

  const getTotalFromScores = (scores) => {
    if (!scores || typeof scores !== 'object') return null;

    const preferredKeys = ['total', 'total_score', 'overall', 'overall_score', '_totalScore'];
    for (const key of preferredKeys) {
      const value = getNumericScoreValue(scores[key]);
      if (value !== null) return value;
    }

    const inferredTotalEntry = Object.entries(scores).find(([key]) =>
      /(^total$|total_score|overall)/i.test(String(key))
    );
    if (inferredTotalEntry) {
      const value = getNumericScoreValue(inferredTotalEntry[1]);
      if (value !== null) return value;
    }

    return null;
  };

  const computeAccurateTotalScore = (result) => {
    const scoringLogic = result?.TestCatalog?.scoringLogic;
    const subscales = scoringLogic?.subscales;
    const responses = result?.responses;

    const calculateSubscale = (subscale) => {
      if (!Array.isArray(subscale?.items) || subscale.items.length === 0) return null;
      const rawSubscaleScore = subscale.items.reduce((sum, itemId) => {
        const responseValue = getResponseValueByItemId(responses, itemId);
        return sum + (responseValue ?? 0);
      }, 0);
      const multiplier = Number(subscale.multiplier ?? 1);
      const validMultiplier = Number.isNaN(multiplier) ? 1 : multiplier;
      return rawSubscaleScore * validMultiplier;
    };

    const deriveFromScoringLogic = () => {
      if (!subscales || typeof subscales !== 'object') return null;

      // Prefer explicit total subscale config (e.g., CSEI total ×2, tests with custom total item sets).
      const explicitTotal = calculateSubscale(subscales.total);
      if (explicitTotal !== null) return explicitTotal;

      // Fallback: sum all non-total subscales.
      let hasValidSubscaleData = false;
      let total = 0;

      Object.entries(subscales).forEach(([subscaleKey, subscale]) => {
        if (/^total$/i.test(subscaleKey)) return;
        const subscaleTotal = calculateSubscale(subscale);
        if (subscaleTotal === null) return;
        hasValidSubscaleData = true;
        total += subscaleTotal;
      });

      return hasValidSubscaleData ? total : null;
    };

    const derivedTotal = deriveFromScoringLogic();
    if (derivedTotal !== null) return derivedTotal;

    const scoreTotal = getTotalFromScores(result?.scores);
    if (scoreTotal !== null) return scoreTotal;

    const interpretationTotal = Number(result?.interpretation?._totalScore);
    if (!Number.isNaN(interpretationTotal)) return interpretationTotal;

    return 0;
  };

  const calculateTrend = (current, previous) => {
    if (!previous) return null;
    const curr = computeAccurateTotalScore(current);
    const prev = computeAccurateTotalScore(previous);
    if (curr < prev) return 'improvement';
    if (curr > prev) return 'decline';
    return 'stable';
  };

  const getSeverityColor = (severity) => ({
    normal:              '#059669',
    minimal:             '#3b82f6',
    mild:                '#f59e0b',
    moderate:            '#ef4444',
    'moderately severe': '#991b1b',
    severe:              '#7f1d1d',
    'extremely severe':  '#450a0a',
  }[severity?.toLowerCase()] || '#2563eb');

  const getSubscaleKeys = (results) => {
    const keys = new Set();
    results.forEach(r => {
      Object.keys(r.interpretation || {}).forEach(k => {
        if (!k.startsWith('_')) keys.add(k);
      });
    });
    return [...keys];
  };

  const getSubMax = (results, key) =>
    Math.max(...results.map(r => r.interpretation?.[key]?.score || 0), 1);

  // ── graph rendering ───────────────────────────────────────────
  const buildGraphData = (results) => {
    const sorted = [...results].sort(
      (a, b) => new Date(a.completedAt) - new Date(b.completedAt)
    );
    return sorted.map((result, idx) => ({
      idx,
      score: computeAccurateTotalScore(result),
      label: new Date(result.completedAt).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric',
      }),
    }));
  };

  const renderTestGraph = (results) => {
    const data = buildGraphData(results);

    if (data.length < 2) {
      return (
        <NoData style={{ padding: '24px 16px' }}>
          <svg viewBox="0 0 24 24" style={{ width: 36, height: 36 }}>
            <line x1="18" y1="20" x2="18" y2="10"/>
            <line x1="12" y1="20" x2="12" y2="4"/>
            <line x1="6"  y1="20" x2="6"  y2="14"/>
            <line x1="2"  y1="20" x2="22" y2="20"/>
          </svg>
          <p>At least 2 attempts are needed to show a trend graph.</p>
        </NoData>
      );
    }

    const width       = Math.max(420, data.length * 90);
    const height      = 200;
    const pad         = { top: 24, right: 20, bottom: 44, left: 40 };
    const graphWidth  = width  - pad.left - pad.right;
    const graphHeight = height - pad.top  - pad.bottom;
    const scores      = data.map(d => d.score);
    const minScore    = Math.min(...scores);
    const maxScore    = Math.max(...scores);
    const range       = Math.max(maxScore - minScore, 1);

    // y-axis grid lines
    const yTicks = 4;
    const yLines = Array.from({ length: yTicks + 1 }, (_, i) => {
      const val = minScore + (range / yTicks) * i;
      const y   = pad.top + ((maxScore - val) / range) * graphHeight;
      return { val: Math.round(val), y };
    });

    const points = data.map((d, i) => {
      const x = pad.left + (i / Math.max(data.length - 1, 1)) * graphWidth;
      const y = pad.top  + ((maxScore - d.score) / range) * graphHeight;
      return { ...d, x, y };
    });

    // filled area under the line
    const areaPath =
      `M ${points[0].x} ${pad.top + graphHeight} ` +
      points.map(p => `L ${p.x} ${p.y}`).join(' ') +
      ` L ${points[points.length - 1].x} ${pad.top + graphHeight} Z`;

    const polyline = points.map(p => `${p.x},${p.y}`).join(' ');

    return (
      <>
        <GraphInfo>
          <span className="title">Total score trend over time</span>
          <span className="meta">{data.length} attempts</span>
        </GraphInfo>

        <GraphSvgWrap>
          <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            style={{ display: 'block' }}
          >
            <defs>
              <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#2563eb" stopOpacity="0.18"/>
                <stop offset="100%" stopColor="#2563eb" stopOpacity="0.01"/>
              </linearGradient>
            </defs>

            {/* grid */}
            {yLines.map(({ y, val }) => (
              <g key={val}>
                <line
                  x1={pad.left} y1={y}
                  x2={width - pad.right} y2={y}
                  stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 3"
                />
                <text
                  x={pad.left - 6} y={y + 4}
                  textAnchor="end" fontSize="9" fill="#94a3b8"
                >
                  {val}
                </text>
              </g>
            ))}

            {/* axes */}
            <line
              x1={pad.left} y1={pad.top + graphHeight}
              x2={width - pad.right} y2={pad.top + graphHeight}
              stroke="#cbd5e1" strokeWidth="1.5"
            />
            <line
              x1={pad.left} y1={pad.top}
              x2={pad.left} y2={pad.top + graphHeight}
              stroke="#e2e8f0" strokeWidth="1.2"
            />

            {/* filled area */}
            <path d={areaPath} fill="url(#areaGrad)"/>

            {/* line */}
            <polyline
              fill="none"
              stroke="#2563eb"
              strokeWidth="2.5"
              strokeLinejoin="round"
              strokeLinecap="round"
              points={polyline}
            />

            {/* dots + labels */}
            {points.map((p, i) => (
              <g key={`pt-${i}`}>
                <circle cx={p.x} cy={p.y} r="5"
                  fill="white" stroke="#2563eb" strokeWidth="2.5"/>
                <text
                  x={p.x} y={height - 16}
                  textAnchor="middle" fontSize="10" fill="#64748b"
                  fontWeight="600"
                >
                  {p.label}
                </text>
                <text
                  x={p.x} y={p.y - 9}
                  textAnchor="middle" fontSize="9.5" fill="#1d4ed8"
                  fontWeight="700"
                >
                  {p.score}
                </text>
              </g>
            ))}
          </svg>
        </GraphSvgWrap>

        <GraphLegend>
          <span className="dot"/>
          <span>Total score per attempt</span>
        </GraphLegend>
      </>
    );
  };

  // ── popstate (Android back) ───────────────────────────────────
  useEffect(() => {
    const handlePop = () => {
      if (graphModal)       { setGraphModal(null);       return; }
      if (selectedClient)   { setSelectedClient(null);   return; }
      if (showMergeModal)   { setShowMergeModal(false);  return; }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [graphModal, selectedClient, showMergeModal]);

  const groupedTests = groupTestsByType();

  // ════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════
  return (
    <PageWrapper>
      <Container>

        {/* ── Header ─────────────────────────────────────────── */}
        <HeaderRow>
          <HeaderText>
            <h1>
              <HeaderIcon><IconUsers /></HeaderIcon>
              Client Management
            </h1>
            <p>Track assessments, demographics &amp; progress</p>
          </HeaderText>

          <MergeButton
            onClick={() => setShowMergeModal(true)}
            disabled={clients.length < 2}
            title={clients.length < 2 ? 'Need at least 2 clients' : 'Merge duplicate clients'}
          >
            <IconMerge />
            Merge Clients
          </MergeButton>
        </HeaderRow>

        {/* ── Toolbar ────────────────────────────────────────── */}
        <ToolBar>
          <SearchInput placeholder="Search clients…" type="text" readOnly />
          <FilterSelect value={testFilter} onChange={e => setTestFilter(e.target.value)}>
            <option value="">All Tests</option>
            {tests.map(t => (
              <option key={t.id} value={t.id}>{t.shortName}</option>
            ))}
          </FilterSelect>
        </ToolBar>

        {autoMergeSummary?.mergedClientsCount > 0 && (
          <AutoMergeNotice>
            <span className="icon">✓</span>
            <span>
              Automatically merged {autoMergeSummary.mergedClientsCount} duplicate client
              {autoMergeSummary.mergedClientsCount === 1 ? '' : 's'} by matching email address.
              <span className="sub">
                Their assessment history is now shown under the primary client record.
              </span>
            </span>
          </AutoMergeNotice>
        )}

        {/* ── Client grid ────────────────────────────────────── */}
        {loading ? (
          <EmptyState>
            <SpinnerRing />
            <p>Loading clients…</p>
          </EmptyState>
        ) : filteredClients.length === 0 ? (
          <EmptyState>
            <EmptyIcon><IconInbox /></EmptyIcon>
            <h3>No Clients Found</h3>
            <p>Start by sending test assignments to clients</p>
          </EmptyState>
        ) : (
          <ClientsGrid>
            {filteredClients.map((client, i) => (
              <ClientCard key={client.id} index={i} onClick={() => viewClientDetails(client)}>
                <CardAvatar>{initials(client)}</CardAvatar>
                <CardContent>
                  <ClientName>{client.firstName} {client.lastName}</ClientName>
                  <ClientMeta>
                    <MetaRow>
                      <span className="key">Age</span>
                      <span className="val">{client.age || 'N/A'}</span>
                    </MetaRow>
                    <MetaRow>
                      <span className="key">Gender</span>
                      <span className="val">{getClientGenderDisplay(client)}</span>
                    </MetaRow>
                    <MetaRow>
                      <span className="key">Email</span>
                      <span className="val">{client.email || '—'}</span>
                    </MetaRow>
                    <MetaRow>
                      <span className="key">Tests</span>
                      <span className="val">
                        <TestsBadge>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                            stroke="white" strokeWidth="2.5" strokeLinecap="round">
                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                          </svg>
                          {client.testsCount || 0}
                        </TestsBadge>
                      </span>
                    </MetaRow>
                  </ClientMeta>
                </CardContent>
              </ClientCard>
            ))}
          </ClientsGrid>
        )}
      </Container>

      {/* ══════════════════════════════════════════════════════════
          MERGE MODAL
      ══════════════════════════════════════════════════════════ */}
      {showMergeModal && (
        <Overlay onClick={() => setShowMergeModal(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <DragHandle />
            <ModalHeader>
              <ModalTitle>
                <IconMerge />
                <span className="name">Merge Clients</span>
              </ModalTitle>
              <CloseBtn onClick={() => setShowMergeModal(false)}>
                <IconX />
              </CloseBtn>
            </ModalHeader>

            <ModalBody>
              <p style={{
                fontSize: '0.84rem', color: '#64748b',
                marginBottom: 14, marginTop: 0, lineHeight: 1.5,
              }}>
                Select 2 or more duplicate clients, then choose which record to keep.
                All assessment history will be merged into the target record.
              </p>

              {/* scrollable client list */}
              <MergeListBox>
                {clients.map(client => {
                  const checked = selectedForMerge.some(c => c.id === client.id);
                  return (
                    <MergeClientRow
                      key={client.id}
                      checked={checked}
                      onClick={() => toggleSelectForMerge(client)}
                    >
                      <MergeCheckbox checked={checked}>
                        {checked && <IconCheck />}
                      </MergeCheckbox>
                      <MergeClientInfo>
                        <div className="name">{client.firstName} {client.lastName}</div>
                        <div className="detail">
                          {client.email || 'No email'} · {client.testsCount || 0} tests
                        </div>
                      </MergeClientInfo>
                    </MergeClientRow>
                  );
                })}
              </MergeListBox>

              {/* target selector — only show when ≥2 selected */}
              {selectedForMerge.length >= 2 && (
                <div style={{ marginTop: 16 }}>
                  <label style={{
                    fontSize: '0.82rem', fontWeight: 700, color: '#0c2d48',
                    display: 'block', marginBottom: 2,
                  }}>
                    Keep this client as the primary record:
                  </label>
                  <TargetSelect
                    value={mergeTarget}
                    onChange={e => setMergeTarget(e.target.value)}
                  >
                    <option value="">Choose target…</option>
                    {selectedForMerge.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.firstName} {c.lastName} ({c.email || 'no email'})
                      </option>
                    ))}
                  </TargetSelect>
                </div>
              )}

              <MergeActions>
                <Btn className="ghost" onClick={() => {
                  setShowMergeModal(false);
                  setSelectedForMerge([]);
                  setMergeTarget('');
                }}>
                  Cancel
                </Btn>
                <Btn
                  className="primary"
                  onClick={mergeClients}
                  disabled={selectedForMerge.length < 2 || !mergeTarget}
                >
                  <IconCheck />
                  Merge {selectedForMerge.length > 0 ? selectedForMerge.length : ''} Clients
                </Btn>
              </MergeActions>
            </ModalBody>
          </ModalBox>
        </Overlay>
      )}

      {/* ══════════════════════════════════════════════════════════
          CLIENT DETAIL MODAL
      ══════════════════════════════════════════════════════════ */}
      {selectedClient && (
        <Overlay onClick={() => setSelectedClient(null)}>
          <ModalBox wide onClick={e => e.stopPropagation()}>
            <DragHandle />
            <ModalHeader>
              <ModalTitle>
                <CardAvatar style={{
                  width: 30, height: 30, borderRadius: 8,
                  fontSize: '0.78rem', margin: 0, flexShrink: 0,
                }}>
                  {initials(selectedClient)}
                </CardAvatar>
                <span className="name">
                  {selectedClient.firstName} {selectedClient.lastName}
                </span>
                <span className="sub">{clientTestHistory.length} results</span>
              </ModalTitle>
              <CloseBtn onClick={() => setSelectedClient(null)}>
                <IconX />
              </CloseBtn>
            </ModalHeader>

            <ModalBody>
              {/* Demographics */}
              <SectionHeading color="#eff6ff" iconColor="#2563eb">
                <div className="icon-wrap"><IconClipboard /></div>
                <h3>Demographics</h3>
              </SectionHeading>

              <DemoGrid>
                {[
                  ['First Name',  selectedClient.firstName],
                  ['Last Name',   selectedClient.lastName],
                  ['Age',         selectedClient.age || 'N/A'],
                  ['Gender',      getClientGenderDisplay(selectedClient)],
                  ['Email',       selectedClient.email || '—'],
                  ['Total Tests', clientTestHistory.length],
                ].map(([l, v]) => (
                  <DemoCard key={l}>
                    <div className="lbl">{l}</div>
                    <div className="val">{v}</div>
                  </DemoCard>
                ))}
              </DemoGrid>

              {/* Assessment history */}
              <SectionHeading color="#f0fdf4" iconColor="#16a34a">
                <div className="icon-wrap"><IconChart /></div>
                <h3>Assessment History</h3>
                <span className="count">{clientTestHistory.length} total</span>
              </SectionHeading>

              {clientTestHistory.length === 0 ? (
                <NoData>
                  <IconClipboard />
                  <p>No assessment history yet</p>
                </NoData>
              ) : (
                Object.entries(groupedTests).map(([testName, results], tIdx) => {
                  const subscaleKeys = getSubscaleKeys(results);

                  return (
                    <TestBlock key={testName} delay={tIdx * 0.08}>
                      <TestBlockHeader>
                        <TestBlockTitle>
                          <div className="dot" />
                          {testName}
                        </TestBlockTitle>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <AssessmentCount>
                            {results.length} attempt{results.length !== 1 ? 's' : ''}
                          </AssessmentCount>

                          {/* ── VIEW GRAPH button — opens popup modal ── */}
                          <GraphToggleBtn
                            onClick={() => setGraphModal({ testName, results })}
                          >
                            <IconChart />
                            View Graph
                          </GraphToggleBtn>
                        </div>
                      </TestBlockHeader>

                      <ScrollHint>
                        <IconSwipe /> Swipe to compare all columns
                      </ScrollHint>

                      <TableWrap>
                        <CompareTable>
                          <THead>
                            <tr>
                              <TH>Date</TH>
                              <TH>Total</TH>
                              {subscaleKeys.map(k => (
                                <TH key={k} style={{ textTransform: 'capitalize' }}>{k}</TH>
                              ))}
                              <TH>vs Prev</TH>
                            </tr>
                          </THead>
                          <tbody>
                            {results.map((result, rIdx) => {
                              const previous   = results[rIdx + 1] || null;
                              const trend      = calculateTrend(result, previous);
                              const interp     = result.interpretation || {};
                              const topSev     = Object.values(interp).find(v => v?.severity)?.severity;
                              const totalColor = getSeverityColor(topSev);
                              const totalScore = computeAccurateTotalScore(result);

                              return (
                                <TR key={result.id} delay={`${rIdx * 0.06}s`}>
                                  <TD>
                                    <DateCell>
                                      <div className="main">
                                        {new Date(result.completedAt).toLocaleDateString('en-US', {
                                          month: 'short', day: 'numeric', year: 'numeric',
                                        })}
                                      </div>
                                      <div className="sub">
                                        {new Date(result.completedAt).toLocaleTimeString('en-US', {
                                          hour: '2-digit', minute: '2-digit',
                                        })}
                                      </div>
                                    </DateCell>
                                  </TD>

                                  <TD>
                                    <TotalScore color={totalColor}>
                                      <span className="num">{totalScore ?? '—'}</span>
                                      <span className="lbl">total</span>
                                    </TotalScore>
                                  </TD>

                                  {subscaleKeys.map((k, kIdx) => {
                                    const val = interp[k];
                                    if (!val) return (
                                      <TD key={k}>
                                        <span style={{ color: '#cbd5e1', fontSize: '0.78rem' }}>—</span>
                                      </TD>
                                    );
                                    const color = getSeverityColor(val.severity);
                                    const max   = getSubMax(results, k);
                                    const pct   = Math.round(((val.score || 0) / max) * 100);
                                    return (
                                      <TD key={k}>
                                        <SubCell>
                                          <SubTop color={color}>
                                            <span className="score">{val.score ?? '—'}</span>
                                            {val.severity && (
                                              <span className="sev">{val.severity}</span>
                                            )}
                                          </SubTop>
                                          <MiniBar
                                            color={color}
                                            delay={`${(rIdx * 0.06) + (kIdx * 0.04)}s`}
                                            style={{ '--pct': `${pct}%` }}
                                          >
                                            <div className="fill" />
                                          </MiniBar>
                                        </SubCell>
                                      </TD>
                                    );
                                  })}

                                  <TD>
                                    {!trend ? (
                                      <span style={{ color: '#cbd5e1', fontSize: '0.72rem' }}>
                                        First
                                      </span>
                                    ) : (
                                      <TrendPill trend={trend}>
                                        {trend === 'improvement' && <><IconTrendDown /> Better</>}
                                        {trend === 'decline'     && <><IconTrendUp />   Higher</>}
                                        {trend === 'stable'      && <><IconMinus />     Same</>}
                                      </TrendPill>
                                    )}
                                  </TD>
                                </TR>
                              );
                            })}
                          </tbody>
                        </CompareTable>
                      </TableWrap>
                    </TestBlock>
                  );
                })
              )}
            </ModalBody>
          </ModalBox>
        </Overlay>
      )}

      {/* ══════════════════════════════════════════════════════════
          GRAPH POPUP MODAL
          — sits above the client detail modal (z-index 1100)
      ══════════════════════════════════════════════════════════ */}
      {graphModal && (
        <Overlay
          style={{ zIndex: 1100 }}
          onClick={() => setGraphModal(null)}
        >
          <ModalBox
            wide="graph"
            onClick={e => e.stopPropagation()}
          >
            <DragHandle />
            <ModalHeader>
              <ModalTitle>
                <IconChart />
                <span className="name">{graphModal.testName} — Score Trend</span>
                <span className="sub">{graphModal.results.length} attempts</span>
              </ModalTitle>
              <CloseBtn onClick={() => setGraphModal(null)}>
                <IconX />
              </CloseBtn>
            </ModalHeader>

            <ModalBody>
              {renderTestGraph(graphModal.results)}
            </ModalBody>
          </ModalBox>
        </Overlay>
      )}
    </PageWrapper>
  );
};

export default Clients;
